import React, { useState } from 'react';

import { formatDistanceToNow } from 'date-fns';
import styles from './workspace.module.css';
import './allPages.css';
import { useNavigate } from 'react-router-dom';
import {
  AlertColor,
  Tooltip,
  Dialog,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DatabaseConnectionDialogComponents from './DataConnection/DatabaseConnectionDialogComponents';

import FetchData from './ServerCall/FetchData';
import { fontSize, palette } from '..';
import { getAccessLevel, isNameAllowed } from './CommonFunctions/CommonFunctions';
import { messages } from './CommonFunctions/aliases';
import { useWorkspace } from '../hooks/useWorkspace';
import { IWorkspace } from '../types/types';
import Logger from '../Logger';
import {
  createWorkspace,
  updateWorkspace,
  deleteWorkspace as _deleteWorkspace,
} from '../api/workspace';

const WorkspaceList = () => {
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [workspaceName, setWorkspaceName] = useState('');
  const [parentId, setParentId] = useState(null);
  const [editWorkspaceId, setEditWorkspaceId] = useState<string | null>(null);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const navigate = useNavigate();
  // const [workspace, setWorkspaces] = useState([]);

  const access = getAccessLevel();

  // State for delete confirmation
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [workspaceIndexToDelete, setWorkspaceIndexToDelete] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState({
    key: 'workspace',
    direction: 'asc',
  }); // state for sorting
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState(''); // State for search input

  const [severity, setSeverity] = useState<AlertColor>('success');
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [testMessage, setTestMessage] = useState<string>('Testing alert');
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<any>(null);
  const { result: workspaces, error, loading, refetch } = useWorkspace();

  React.useEffect(() => {
    localStorage.removeItem('dashboard_clipboard');
  }, []);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (isModalOpen) {
          setIsModalOpen(false);
          setErrorMessage('');
        }
        if (isEditModalOpen) {
          setIsEditModalOpen(false);
          setErrorMessage('');
        }
        if (showDeleteConfirmation) {
          setShowDeleteConfirmation(false);
          setWorkspaceIndexToDelete(null);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isModalOpen, isEditModalOpen, showDeleteConfirmation]);

  const handleWorkspaceClick = (wid: string, wname: string) => {
    localStorage.setItem('workspace', wname);
    navigate(`/workspace/${wid}`, { state: { wname } });
  };

  const handleCreateWorkspace = async () => {
    if (workspaceName.length > 40) {
      setErrorMessage('Workspace name cannot exceed 40 characters.');
      return;
    }
    const isValidName = isNameAllowed(workspaceName);

    if (!isValidName) {
      setErrorMessage(messages.workspace.wrongName);
      return;
    }
    setIsLoading(true);
    try {
      const response = await createWorkspace(workspaceName);

      if (!response.isError) {
        refetch();
        setIsModalOpen(false);
        setIsDropdownOpen(false);
        setWorkspaceName('');
        setErrorMessage('');
      } else {
        setErrorMessage(response.message || 'Failed to create workspace.');
      }
    } catch (error) {
      console.error('Error creating workspace:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredWorkspaces = React.useMemo(() => {
    return workspaces.filter((workspace) =>
      workspace.name.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [workspaces, searchQuery]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleSelectAll = () => {
    setSelectedWorkspaces(workspaces.map((workspace: any) => workspace.id));
  };

  const sortedAndFilteredWorkspaces = React.useMemo(() => {
    if (!sortConfig.key) return [...filteredWorkspaces];

    const sortedItems = [...filteredWorkspaces].sort((a, b) => {
      const getValue = (item: IWorkspace) => {
        switch (sortConfig.key) {
          case 'workspace':
            return item.name.toLowerCase();
          case 'created at':
            return new Date(item.createdAt);
          case 'created by':
            return item.createdBy.toLowerCase();
          default:
            return '';
        }
      };

      const aValue = getValue(a);
      const bValue = getValue(b);

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sortedItems;
  }, [filteredWorkspaces, sortConfig]);

  const handleSort = (key: any) => {
    const newDirection = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction: newDirection });
  };

  const getSortIcon = (key: any) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? (
        <img className="headerIcon-up" src="/sort-up.png" alt="Ascending" />
      ) : (
        <img className="headerIcon-down" src="/sort-down.png" alt="Descending" />
      );
    }
    return <img className="headerIcon" src="/sort.png" alt="Default" />;
  };

  const handleWorkspaceSelection = (id: string) => {
    if (selectedWorkspaces.includes(id)) {
      // If already selected, remove it from the list
      setSelectedWorkspaces(selectedWorkspaces.filter((workspaceId) => workspaceId !== id));
    } else {
      // Otherwise, add it to the list
      setSelectedWorkspaces([...selectedWorkspaces, id]);
    }
  };
  const handleEditWorkspace = async () => {
    if (!editWorkspaceId) return;

    if (workspaceName.length > 40) return;

    const isValidName = isNameAllowed(workspaceName);

    if (!isValidName) {
      setErrorMessage(messages.workspace.wrongName);
      return;
    }
    setIsLoading(true);
    try {
      const response = await updateWorkspace(editWorkspaceId, workspaceName);
      if (response.message === 'Name is already taken') {
        setErrorMessage('A workspace exists with the same name. Please try other names.');
        setWorkspaceName(''); // Reset the workspace name
      } else if (!response.isError) {
        refetch();
        // mutate();
        setIsEditModalOpen(false);
        setWorkspaceName('');
        setEditWorkspaceId(null);
      } else {
        Logger('error', 'Failed to update workspace:', response.data || 'Unknown error');
      }
    } catch (error) {
      Logger('error', 'Error updating workspace:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteWorkspace = (workspaceId: string, name: any) => {
    setWorkspaceIndexToDelete(workspaceId);
    setWorkspaceName(name);
    setShowDeleteConfirmation(true);
  };

  const handleDeleteWorkspace = async () => {
    if (!workspaceIndexToDelete) return;

    setIsLoading(true);
    try {
      const response = await _deleteWorkspace(workspaceIndexToDelete);
      //TODO:
      if (response.isError) {
        setOpenAlert(true);
        setTestMessage("Workspace can't be deleted before deleting all of its contents first.");
        setSeverity('error');
      }
      refetch();
    } catch (error) {
      console.error('Error fetching workspace:', error);
    } finally {
      setIsLoading(false);
      setShowDeleteConfirmation(false);
      setWorkspaceIndexToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirmation(false);
    setWorkspaceIndexToDelete(null);
  };

  const openEditModal = (id: string, currentName: string, currentParentId: any) => {
    setEditWorkspaceId(id);
    setWorkspaceName(currentName);
    setParentId(currentParentId);
    setIsEditModalOpen(true);
  };

  const openAccessModal = (workspaceId: string) => {
    // navigate(`/workspace/access/${parentId}`, { state: { workspaceId } });
    navigate(`/workspace/access/${workspaceId}`, { state: { parentId } });
  };

  if (error)
    return (
      <div className="loading-error-info">
        <p
          className="loading-default-text"
          style={{
            fontSize: fontSize.medium,
            color: palette.primary.contrastText,
          }}
        >
          Error loading workspaces.
        </p>
      </div>
    );

  return (
    <div className={`${styles.workspaceContainer} workspace-container`}>
      <div
        className="heading"
        style={{
          height: '50px',
          border: '1px solid transparent',
          visibility: 'hidden',
        }}
      ></div>

      {/* <Link to="/admin/users">
        <button className={styles.backLink}>Back</button>
      </Link> */}
      <div className="workspace-button-add-search-container">
        <div className="workspace-action-buttons">
          {(access === 'community' ||
            access === 'account_admin' ||
            access === 'admin' ||
            workspaces.some(
              (workspace: any) =>
                (workspace.roleId < 3 && workspace.roleId > 0) || workspace.roleId === 8,
            )) && (
            <div
              className={styles.dropdown}
              onMouseEnter={() => setIsDropdownOpen(true)}
              onMouseLeave={() => setIsDropdownOpen(false)}
            >
              <button
                className={`${styles.createWorkspace} create-workspace`}
                // onClick={() => setIsModalOpen(true)}
                style={{
                  fontSize: fontSize.medium,
                  color: palette.primary.main,
                }}
              >
                <img
                  className="addIcon addIconDefault"
                  src="/add_green.png"
                  alt="Add Users Logo"
                  style={{ width: '16px', height: '16px' }}
                />
                &nbsp;New
              </button>

              {isDropdownOpen && (
                <div className={styles.dropdownContent}>
                  <button
                    className={styles.dropdownItem}
                    onClick={() => setIsModalOpen(true)}
                    style={{
                      color: palette.primary.contrastText,
                      fontSize: fontSize.medium,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <img
                      src="folder_work.svg"
                      alt="Folder Icon"
                      style={{
                        width: '17px',
                        height: '17px',
                        color: palette.primary.contrastText,
                        fontSize: fontSize.medium,
                      }}
                      className={styles.dropdownIcon}
                    ></img>
                    Workspace
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="workspaceSelectActionContainer">
          <div className="workspaceActionsContainer">
            {/* Future Update
        <p onClick={handleSelectAll} style={{ cursor: 'pointer' }}>
          Select All
        </p>

        {selectedWorkspaces.length > 0 && (
          <div className='workspaceActionsCountContainer'>    
          <p onClick={() => setSelectedWorkspaces([])} style={{ cursor: 'pointer', marginLeft: "10px" }}>Deselect All</p>
          <span>{selectedWorkspaces.length > 0 && `${selectedWorkspaces.length} selected`}</span>
          <p style={{ marginLeft: "10px" }}>Actions</p>
          </div>
        )}*/}
          </div>

          <div className="workspaceSearchContainer">
            <input
              type="text"
              placeholder="Search Workspace"
              value={searchQuery}
              onChange={handleSearch}
              style={{
                color: palette.primary.contrastText,
                fontSize: fontSize.medium,
              }}
              className="workspaceSearchInput"
            />
            <img src="/glass.png" alt="Search Icon" className="workspaceSearchIcon" />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
        </div>
      ) : workspaces.length === 0 && searchQuery.length === 0 ? (
        <div className="no-user-info">
          <p
            className="loading-default-text"
            style={{
              color: palette.primary.contrastText,
              fontSize: fontSize.medium,
            }}
          >
            Currently, there are no workspaces created.
          </p>
        </div>
      ) : (
        <TableContainer className="workspace-tableContainer">
          <Table className={`${styles.workspaceTable} workspace-table`}>
            <TableHead>
              <TableRow>
                <TableCell></TableCell>

                <TableCell style={{ fontSize: fontSize.medium }}>Type</TableCell>

                <TableCell
                  onClick={() => handleSort('workspace')}
                  style={{ fontSize: fontSize.medium }}
                >
                  Workspace <span className="icon-wrapper">{getSortIcon('workspace')}</span>
                </TableCell>

                <TableCell
                  onClick={() => handleSort('created by')}
                  style={{ fontSize: fontSize.medium }}
                >
                  Created by <span className="icon-wrapper">{getSortIcon('created by')}</span>
                </TableCell>

                <TableCell
                  onClick={() => handleSort('created at')}
                  style={{ fontSize: fontSize.medium }}
                >
                  Created at <span className="icon-wrapper">{getSortIcon('created at')}</span>
                </TableCell>

                <TableCell style={{ fontSize: fontSize.medium }}>Actions</TableCell>
              </TableRow>
            </TableHead>

            {sortedAndFilteredWorkspaces.length === 0 && searchQuery.length !== 0 ? (
              <TableContainer className="no-user-info-insideTable">
                <p
                  className="loading-default-text"
                  style={{
                    fontSize: fontSize.medium,
                    color: palette.primary.contrastText,
                  }}
                >
                  No workspace found.
                </p>
              </TableContainer>
            ) : (
              <TableBody>
                {sortedAndFilteredWorkspaces.map((workspace) => (
                  <TableRow
                    key={workspace.id}
                    onMouseEnter={() => setHoveredRowId(workspace.id)}
                    onMouseLeave={() => setHoveredRowId(null)}
                    className={selectedWorkspaces.includes(workspace.id) ? 'selectedRow' : ''}
                  >
                    <TableCell>
                      {/* <input
                        type="checkbox"
                        checked={selectedWorkspaces.includes(workspace.id)}
                        onChange={() => handleWorkspaceSelection(workspace.id)}
                        style={{ width: "16px", height: "16px" }}
                      /> */}
                    </TableCell>

                    <TableCell>
                      <img
                        src="/folder_work.svg"
                        className={styles.icon}
                        style={{ width: '17px', height: '17px' }}
                        alt="Folder Icon"
                      />
                    </TableCell>

                    <TableCell
                      className={styles.nameHyperlink}
                      data-testId={`${workspace.name}_name`}
                      style={{
                        fontSize: fontSize.medium,
                        color: palette.primary.contrastText,
                      }}
                      onClick={() => handleWorkspaceClick(workspace.id, workspace.name)}
                    >
                      {workspace.name}
                    </TableCell>

                    <TableCell
                      style={{
                        fontSize: fontSize.medium,
                        color: palette.primary.contrastText,
                      }}
                    >
                      {workspace.createdBy}
                    </TableCell>

                    <TableCell
                      style={{
                        fontSize: fontSize.medium,
                        color: palette.primary.contrastText,
                      }}
                    >
                      {' '}
                      {workspace.createdAt
                        ? formatDistanceToNow(new Date(workspace.createdAt), {
                            addSuffix: true,
                          })
                        : 'N/A'}
                    </TableCell>

                    <TableCell>
                      {(workspace.roleId < 5 && workspace.roleId > 0) || workspace.roleId === 8 ? (
                        <div className="workspace-img-icon">
                          {workspace.roleId < 3 || workspace.roleId === 8 ? (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditModal(
                                    workspace.id,
                                    workspace.name,
                                    workspace.parentWorkspaceId,
                                  );
                                }}
                                data-testId={`${workspace.name}_rename`}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                }}
                              >
                                <Tooltip
                                  title="Rename"
                                  slotProps={{
                                    tooltip: {
                                      sx: {
                                        fontSize: '12px',
                                      },
                                    },
                                  }}
                                >
                                  <img
                                    src={
                                      hoveredRowId === workspace.id ? '/edit.png' : '/edit_grey.png'
                                    }
                                    alt="Edit"
                                    style={{
                                      marginTop: '1px',
                                      width: '16px',
                                      height: '16px',
                                    }}
                                  />
                                </Tooltip>
                              </button>

                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteWorkspace(workspace.id, workspace.name);
                                }}
                                data-testId={`${workspace.name}_delete`}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                }}
                              >
                                <Tooltip
                                  title="Delete"
                                  slotProps={{
                                    tooltip: {
                                      sx: {
                                        fontSize: '12px',
                                      },
                                    },
                                  }}
                                >
                                  <img
                                    src={
                                      hoveredRowId === workspace.id
                                        ? '/delete_red.png'
                                        : '/delete_grey.png'
                                    }
                                    alt="Delete"
                                    style={{
                                      marginTop: '1px',
                                      width: '17px',
                                      height: '17px',
                                      fontSize: fontSize.medium,
                                      color: palette.primary.contrastText,
                                    }}
                                  />
                                </Tooltip>
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'not-allowed',
                                }}
                              >
                                <Tooltip
                                  title="Rename"
                                  slotProps={{
                                    tooltip: {
                                      sx: {
                                        fontSize: '12px',
                                      },
                                    },
                                  }}
                                >
                                  <img
                                    src={'/edit_grey.png'}
                                    alt="Edit"
                                    style={{
                                      marginTop: '1px',
                                      width: '16px',
                                      height: '16px',
                                      pointerEvents: 'none',
                                    }}
                                  />
                                </Tooltip>
                              </button>

                              <button
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'not-allowed',
                                }}
                              >
                                <Tooltip
                                  title="Delete"
                                  slotProps={{
                                    tooltip: {
                                      sx: {
                                        fontSize: '12px',
                                      },
                                    },
                                  }}
                                >
                                  <img
                                    src={'/delete_grey.png'}
                                    alt="Delete"
                                    style={{
                                      marginTop: '1px',
                                      width: '17px',
                                      height: '17px',
                                      fontSize: fontSize.medium,
                                      color: palette.primary.contrastText,
                                      pointerEvents: 'none',
                                    }}
                                  />
                                </Tooltip>
                              </button>
                            </>
                          )}

                          {workspace.roleId < 5 && workspace.roleId > 0 ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openAccessModal(workspace.id);
                              }}
                              data-testId={`${workspace.name}_access`}
                              style={{
                                background: 'none',
                                border: 'none',
                              }}
                            >
                              <Tooltip
                                title="Manage Access"
                                slotProps={{
                                  tooltip: {
                                    sx: {
                                      fontSize: '12px',
                                    },
                                  },
                                }}
                              >
                                <img
                                  src={
                                    hoveredRowId === workspace.id
                                      ? '/access.png'
                                      : '/access_grey.png'
                                  }
                                  alt="Access"
                                  style={{
                                    marginBottom: '-2.5px',
                                    width: '20px',
                                    height: '20px',
                                  }}
                                />
                              </Tooltip>
                            </button>
                          ) : (
                            workspace.roleId !== 8 && (
                              <button
                                style={{
                                  background: 'none',
                                  border: 'none',
                                }}
                              >
                                <Tooltip
                                  title="Manage Access"
                                  slotProps={{
                                    tooltip: {
                                      sx: {
                                        fontSize: '12px',
                                      },
                                    },
                                  }}
                                >
                                  <img
                                    src={'/access_grey.png'}
                                    alt="Access"
                                    style={{
                                      marginBottom: '-2.5px',
                                      width: '20px',
                                      height: '20px',
                                      pointerEvents: 'none',
                                    }}
                                  />
                                </Tooltip>
                              </button>
                            )
                          )}
                        </div>
                      ) : (
                        <div className="workspace-img-icon">
                          <button
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'not-allowed',
                            }}
                          >
                            <Tooltip
                              title="Rename"
                              slotProps={{
                                tooltip: {
                                  sx: {
                                    fontSize: '12px',
                                  },
                                },
                              }}
                            >
                              <img
                                src={'/edit_grey.png'}
                                alt="Edit"
                                style={{
                                  marginTop: '1px',
                                  width: '16px',
                                  height: '16px',
                                  pointerEvents: 'none',
                                }}
                              />
                            </Tooltip>
                          </button>

                          <button
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'not-allowed',
                            }}
                          >
                            <Tooltip
                              title="Delete"
                              slotProps={{
                                tooltip: {
                                  sx: {
                                    fontSize: '12px',
                                  },
                                },
                              }}
                            >
                              <img
                                src={'/delete_grey.png'}
                                alt="Delete"
                                style={{
                                  marginTop: '1px',
                                  width: '17px',
                                  height: '17px',
                                  fontSize: fontSize.medium,
                                  color: palette.primary.contrastText,
                                  pointerEvents: 'none',
                                }}
                              />
                            </Tooltip>
                          </button>

                          {workspace.roleId < 3 && workspace.roleId > 0 ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openAccessModal(workspace.id);
                              }}
                              style={{
                                background: 'none',
                                border: 'none',
                              }}
                            >
                              <Tooltip
                                title="Manage Access"
                                slotProps={{
                                  tooltip: {
                                    sx: {
                                      fontSize: '12px',
                                    },
                                  },
                                }}
                              >
                                <img
                                  src={
                                    hoveredRowId === workspace.id
                                      ? '/access.png'
                                      : '/access_grey.png'
                                  }
                                  alt="Access"
                                  style={{
                                    marginBottom: '-2.5px',
                                    width: '20px',
                                    height: '20px',
                                  }}
                                />
                              </Tooltip>
                            </button>
                          ) : (
                            workspace.roleId !== 8 && (
                              <button
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'not-allowed',
                                }}
                              >
                                <Tooltip
                                  title="Manage Access"
                                  slotProps={{
                                    tooltip: {
                                      sx: {
                                        fontSize: '12px',
                                      },
                                    },
                                  }}
                                >
                                  <img
                                    src={'/access_grey.png'}
                                    alt="Access"
                                    style={{
                                      marginBottom: '-2.5px',
                                      width: '20px',
                                      height: '20px',
                                      pointerEvents: 'none',
                                    }}
                                  />
                                </Tooltip>
                              </button>
                            )
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </TableContainer>
      )}

      <Dialog open={showDeleteConfirmation} onClose={cancelDelete}>
        <div className="workspace-remove-modal">
          <div className="workspace-remove-modalContent-close">
            <Tooltip
              title="Close"
              slotProps={{
                tooltip: {
                  sx: {
                    fontSize: '12px',
                  },
                },
              }}
            >
              <CloseIcon
                onClick={() => setShowDeleteConfirmation(false)}
                sx={{
                  fontSize: 'large',
                  color: '#545454',
                  cursor: 'pointer',
                  '&:hover': {
                    color: 'red',
                  },
                }}
              />
            </Tooltip>
          </div>
          <div className="workspace-remove-modalContent-p">
            <p
              style={{
                fontSize: fontSize.medium,
                color: palette.primary.contrastText,
              }}
            >
              Are you sure you want to delete the workspace {workspaceName}?
            </p>
          </div>

          <div className="workspace-remove-modalContent-Buttons">
            <button
              onClick={cancelDelete}
              className="workspace-remove-modalCancel"
              style={{ fontSize: fontSize.medium }}
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteWorkspace}
              className="workspace-remove-modalConfirm"
              style={{ fontSize: fontSize.medium }}
            >
              Confirm
            </button>
          </div>
        </div>
      </Dialog>

      {/* Create Workspace Modal */}
      {isModalOpen && (
        <div className={styles.workspaceModal}>
          <div className={styles.workspaceModalContent}>
            <h3
              style={{
                fontSize: fontSize.large,
                color: palette.primary.contrastText,
              }}
            >
              Create Workspace
            </h3>
            <div className={styles.workspaceModalContentInput}>
              <input
                type="text"
                // value={""}
                autoFocus
                onChange={(e) => {
                  const value = e.target.value;
                  setWorkspaceName(value);

                  if (value.length > 40) {
                    setErrorMessage('Workspace name cannot exceed 40 characters.');
                  } else {
                    setErrorMessage('');
                  }
                }}
                placeholder="Workspace Name"
                style={{
                  fontSize: fontSize.medium,
                  color: palette.primary.contrastText,
                  border: errorMessage ? '1px solid red' : '',
                }}
              />
            </div>
            {/* Error message section */}
            {errorMessage && (
              <div
                className={styles.errorMessage}
                style={{
                  fontSize: fontSize.small,
                  color: 'red',
                }}
              >
                {errorMessage}
              </div>
            )}
            <div className={styles.workspaceModalButtons}>
              <button
                className={styles.workspaceModalCancel}
                onClick={() => {
                  setIsModalOpen(false);
                  setErrorMessage('');
                }}
                style={{ fontSize: fontSize.medium }}
              >
                Cancel
              </button>
              <button
                className={styles.workspaceSaveButton}
                onClick={handleCreateWorkspace}
                style={{ fontSize: fontSize.medium, color: 'white' }}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Workspace Modal  */}
      {isEditModalOpen && (
        <div className={styles.workspaceModal}>
          <div className={styles.workspaceModalContent}>
            <h3
              style={{
                fontSize: fontSize.large,
                color: palette.primary.contrastText,
              }}
            >
              Rename
            </h3>
            <div
              className={styles.workspaceModalContentInput}
              style={{ height: errorMessage ? '30px' : '40px' }}
            >
              <input
                type="text"
                style={{
                  fontSize: fontSize.medium,
                  color: palette.primary.contrastText,
                  border: workspaceName.length > 40 ? '1px solid red' : '',
                }}
                value={workspaceName}
                onChange={(e) => {
                  const value = e.target.value;
                  setWorkspaceName(value);

                  if (value.length > 40) {
                    setErrorMessage('Workspace name cannot exceed 40 characters.');
                  } else {
                    setErrorMessage('');
                  }
                }}
                placeholder="Workspace Name"
              />
            </div>
            <div className={styles.errorMessageContainer}>
              {errorMessage && (
                <p style={{ color: 'red', fontSize: fontSize.small, marginTop: '18px' }}>
                  {errorMessage}
                </p>
              )}
            </div>
            <div className={styles.workspaceModalButtons}>
              <button
                className={styles.workspaceModalCancel}
                onClick={() => {
                  setIsEditModalOpen(false);
                  setErrorMessage('');
                }}
                style={{ fontSize: fontSize.medium }}
              >
                Cancel
              </button>
              <button
                className={styles.workspaceSaveButton}
                onClick={handleEditWorkspace}
                style={{ fontSize: fontSize.medium }}
              >
                Rename
              </button>
            </div>
          </div>
        </div>
      )}

      <DatabaseConnectionDialogComponents
        onCloseAlert={() => {
          setOpenAlert(false);
          setTestMessage('');
        }}
        severity={severity}
        testMessage={testMessage}
        openAlert={openAlert}
      />
    </div>
  );
};

export default WorkspaceList;

// import React, { useState } from "react";
// import useSWR from "swr";
// import axios from "axios";
// import styles from "./workspace.module.css";
// import { Link } from "react-router-dom";
// const token = "eyJhbGciOiJIUzUxMiJ9.eyJzdWIiOiJhY2hpbnR5YS5yYWpAc2lsemlsYS5jb20iLCJhdWRpZW5jZSI6IndlYiIsImFjY2VzcyI6ImFjY291bnRfYWRtaW4iLCJpYXQiOjE3MjM2MTczOTQsInRlbmFudCI6InNpbHppbGEiLCJleHAiOjE3MjM2NzQ5OTR9.w7A0D0KnMrrhwQV02-c6n4psUohlZw92C0t3JOlMkTqrcE9HuLuPVqhBNBQ-RbnNQNvGl_sGRy_DdIAIGgEacQ";
// localStorage.setItem('accessToken', token)
// // Define a fetcher function using axios
// const fetcher = (url) =>
//   axios
//     .get(url, {
//       headers: {
//         Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
//         "Content-Type": "application/json",
//       },
//     })
//     .then((res) => res.data);

// const WorkspaceList = () => {
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [isEditModalOpen, setIsEditModalOpen] = useState(false);
//   const [workspaceName, setWorkspaceName] = useState("");
//   const [editWorkspaceId, setEditWorkspaceId] = useState(null);
//   const [parentId, setParentId] = useState(null);
//   const [hoveredRowId, setHoveredRowId] = useState(null);

//   // Use SWR for fetching workspaces
//   const { data: workspaces = [], error, isLoading, mutate } = useSWR(
//     "${serverEndPoint}workspace",
//     fetcher
//   );

// const handleCreateWorkspace = async () => {
//   try {
//     const response = await axios.post(
//       "${serverEndPoint}workspace/create",
//       {
//         name: workspaceName,
//         parentId: parentId || null,
//       },
//       {
//         headers: {
//           "Content-Type": "application/json",
//           Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
//         },
//       }
//     );

//     if (response.status === 200) {
//       console.log("Workspace created successfully");
//       // Mutate the SWR cache to update workspaces
//       mutate();
//       setIsModalOpen(false); // Close the modal
//       setWorkspaceName(""); // Reset the workspace name
//     } else {
//       console.error("Failed to create workspace");
//     }
//   } catch (error) {
//     console.error("Error creating workspace:", error);
//   }
// };

// const handleEditWorkspace = async () => {
//   try {
//     const response = await axios.put(
//       "${serverEndPoint}workspace/update",
//       {
//         workspaceId: editWorkspaceId,
//         name: workspaceName,
//         parentId: parentId,
//       },
//       {
//         headers: {
//           "Content-Type": "application/json",
//           Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
//         },
//       }
//     );

//     if (response.status === 200) {
//       console.log("Workspace updated successfully");
//       mutate(); // Mutate the SWR cache to update workspaces
//       setIsEditModalOpen(false);
//       setWorkspaceName("");
//       setEditWorkspaceId(null);
//     } else {
//       console.error("Failed to update workspace");
//     }
//   } catch (error) {
//     console.error("Error updating workspace:", error);
//   }
// };

// const handleDeleteWorkspace = async (id) => {
//   try {
//     const response = await axios.delete(
//       `${serverEndPoint}workspace/delete/${id}`,
//       {
//         headers: {
//           "Content-Type": "application/json",
//           Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
//         },
//       }
//     );

//     if (response.status === 200) {
//       console.log("Workspace deleted successfully");
//       mutate(); // Mutate the SWR cache to update workspaces
//     } else {
//       console.error("Failed to delete workspace");
//     }
//   } catch (error) {
//     console.error("Error deleting workspace:", error);
//   }
// };

// const openEditModal = (id, currentName, currentParentId) => {
//   setEditWorkspaceId(id);
//   setWorkspaceName(currentName);
//   setParentId(currentParentId);
//   setIsEditModalOpen(true);
// };

//   if (error) return <p>Error loading workspaces.</p>;

//   return (
//     <>
//       <Link to="/admin/users">
//         <button className={styles.backLink}>Back</button>
//       </Link>
//       {/* <div className={styles.actionButtons}>
//         <button
//           className={styles.createWorkspace}
//           onClick={() => setIsModalOpen(true)}
//         >
//           <img
//             className="addIcon addIconDefault"
//             src="/add.png"
//             alt="Add Users Logo"
//             width={20}
//             height={20}
//           />
//           Create Workspace
//         </button>
//       </div> */}
//       <div className={styles.workspaceList}>
//         {isLoading ? (
//           <p>Loading workspaces...</p>
//         ) : workspaces.length === 0 ? (
//           <p className={styles.noWorkspaces}>
//             Currently, there are no workspaces created.
//           </p>
//         ) : (
//           <table className={styles.workspaceTable}>
//             <thead>
//               <tr>
//                 <th></th>
//                 <th>Workspace</th>
//                 {/* <th className={styles.icons}><img src='/subworkspace.png' alt='' height={20} width={20} /></th>
//                 <th className={styles.icons}><img src='/database_header.png' alt='' height={20} width={20} /></th>
//                 <th className={styles.icons}><img src='/dataset_header.png' alt='' height={20} width={20} /></th>
//                 <th className={styles.icons}><img src='/flatfile_header.png' alt='' height={20} width={20} /></th>
//                 <th className={styles.icons}><img src='/playbook_header.png' alt='' height={20} width={20} /></th> */}
//                 <th>Modified at</th>
//                 <th>Modified by</th>
//                 {/* <th>Actions</th> */}
//               </tr>
//             </thead>
//             <tbody>
//               {workspaces.map((workspace) => (
//                 <tr
//                   key={workspace.id}
//                   onMouseEnter={() => setHoveredRowId(workspace.id)}
//                   onMouseLeave={() => setHoveredRowId(null)}
//                 >
//                   <td><img src='/open_folder.png' className={styles.icon} height={20} width={20}></img></td>
//                   <td>
//                     <Link to={`/subworkspace/${workspace.id}`}>
//                       {workspace.name}
//                     </Link>
//                   </td>
//                   {/* <td>{workspace.subWorkspaceCount}</td>
//                   <td>{workspace.dbConnectionCount}</td>
//                   <td>{workspace.datasetCount}</td>
//                   <td>{workspace.flatfileCount}</td>
//                   <td>{workspace.playbookCount}</td> */}
//                   <td>{workspace.createdAt}</td>
//                   <td>{workspace.createdBy}</td>
//     {/* <td>
//       <img
//         src={
//           hoveredRowId === workspace.id
//             ? "/edit.png"
//             : "/edit_grey.png"
//         }
//         alt="Edit"
//         className={`${styles.actionIcon} ${styles.editIcon}`}
//         onClick={(e) => {
//           e.stopPropagation();
//           openEditModal(
//             workspace.id,
//             workspace.workspaceName,
//             workspace.parentId
//           );
//         }}
//         height={20}
//         width={20}
//       />
//       <img
//         src={
//           hoveredRowId === workspace.id
//             ? "/delete.png"
//             : "/delete_grey.png"
//         }
//         alt="Delete"
//         className={`${styles.actionIcon} ${styles.deleteIcon}`}
//         onClick={(e) => {
//           e.stopPropagation();
//           handleDeleteWorkspace(workspace.id);
//         }}
//         height={20}
//         width={20}
//       />
//     </td> */}
//   </tr>
// ))}

//             </tbody>
//           </table>
//         )}
//       </div>
// {/* Create Workspace Modal */}
// {isModalOpen && (
//   <div className={styles.modal}>
//     <div className={styles.modalContent}>
//       <h2>Create Workspace</h2>
//       <input
//         type="text"
//         value={workspaceName}
//         onChange={(e) => setWorkspaceName(e.target.value)}
//         placeholder="Workspace Name"
//       />
//       <button onClick={handleCreateWorkspace}>Create</button>
//       <button onClick={() => setIsModalOpen(false)}>Cancel</button>
//     </div>
//   </div>
// )}

// {/* Edit Workspace Modal */}
// {isEditModalOpen && (
//   <div className={styles.modal}>
//     <div className={styles.modalContent}>
//       <h2>Edit Workspace</h2>
//       <input
//         type="text"
//         value={workspaceName}
//         onChange={(e) => setWorkspaceName(e.target.value)}
//         placeholder="Workspace Name"
//       />
//       <button onClick={handleEditWorkspace}>Save</button>
//       <button onClick={() => setIsEditModalOpen(false)}>Cancel</button>
//     </div>
//   </div>
// )}

//     </>
//   );
// };

// export default WorkspaceList;
